import { type CSSProperties } from "react";
import { Card, EmptyState } from "../primitives";
import { ActivityRow } from "../cockpit/ActivityPanel";
import { useActivitiesFor } from "../../hooks/use-activities";

interface RoutineRunHistoryProps {
  routineId: string;
  limit?: number;
}

/**
 * Sprint 26.1 — per-routine run history. Each recorded run writes an Activity
 * row (via the Run flow's record step) tagged with `routine_ref`, so this is
 * just `list_activities_for` over the routine target. Reuses {@link ActivityRow}
 * for a consistent timeline look.
 */
export function RoutineRunHistory({ routineId, limit = 20 }: RoutineRunHistoryProps) {
  const query = useActivitiesFor({ kind: "routine", id: routineId }, limit);
  const runs = query.data ?? [];

  return (
    <Card>
      <div style={header}>Run history</div>
      {query.isLoading ? (
        <div style={muted}>Loading runs…</div>
      ) : runs.length === 0 ? (
        <EmptyState>
          No runs recorded yet. Use "Run now" to compile the procedure; recording the outcome logs a run here.
        </EmptyState>
      ) : (
        <div data-testid="routine-run-history">
          {runs.map((a) => (
            <ActivityRow key={a.id} activity={a} />
          ))}
        </div>
      )}
    </Card>
  );
}

const header: CSSProperties = {
  fontSize: 14,
  fontWeight: 500,
  color: "var(--color-text-primary)",
  marginBottom: 12,
};

const muted: CSSProperties = {
  fontSize: 13,
  color: "var(--color-text-tertiary)",
};
